import type { Editor } from "@tiptap/core"
import Placeholder from "@tiptap/extension-placeholder"
import type { Node as ProsemirrorNode } from "prosemirror-model"

import { ListItem } from "../coreExtensions"
import { Paragraph } from "../extensions"
import EditorParagraph from "../extensions/Paragraph/Paragraph"
import {
  type ExtensionPlaceholder,
  type NodeExtension,
  NodeUtils
} from "../models"

interface PlaceholderArgs {
  placeholderByNodeType: Record<string, ExtensionPlaceholder>
  unfocusedPlaceholder: string | undefined
  nodeExtensions: readonly NodeExtension[]
}

// eslint-disable-next-line import/no-default-export
export default (args: PlaceholderArgs) =>
  Placeholder.configure({
    includeChildren: true,
    placeholder: (props) => getPlaceholder(props, args),
    showOnlyCurrent: false,
    showOnlyWhenEditable: false
  })

function getPlaceholder(props: PlaceholderProps, args: PlaceholderArgs) {
  const { editor, hasAnchor, pos, node } = props
  const { placeholderByNodeType, unfocusedPlaceholder, nodeExtensions } = args

  const isList = !NodeUtils.children.exists(
    editor.state.doc,
    (child) => child === node
  )

  const isEditable = editor.isEditable

  if (
    unfocusedPlaceholder &&
    !editor.isFocused &&
    editor.isEmpty &&
    pos === 0
  ) {
    return unfocusedPlaceholder
  }

  const isNotParagraph = node.type.name !== Paragraph.name

  const isPersisted =
    (editor.isFocused && hasAnchor) || isNotParagraph || isList

  const hasCommands =
    nodeExtensions.filter((extension) => extension !== Paragraph).length > 1

  function getShouldShow(placeholder: string | undefined) {
    if (placeholder === EditorParagraph.placeholder)
      return isEditable && isPersisted && hasCommands

    return isEditable && isPersisted
  }

  function getFocusedPlaceholder() {
    if (isList) return ListItem.placeholder

    const p = placeholderByNodeType[node.type.name]
    if (typeof p === "string") return p
    return p?.(node.attrs)
  }

  const focusedPlaceholder = getFocusedPlaceholder()

  const shouldShow = getShouldShow(focusedPlaceholder)

  return (shouldShow && focusedPlaceholder) || ""
}

interface PlaceholderProps {
  editor: Editor
  hasAnchor: boolean
  node: ProsemirrorNode
  pos: number
}
